import React, { useEffect, useRef, useState } from "react";
import ProfessionalChart from "../components/ProfessionalChart";
import { getStockBars, getStockSnapshot } from "../marketData/marketDataApi";
import ProfessionalChartWorkspace from "./ProfessionalChartWorkspace";
import { drawingReducer } from "./drawingModel";
import "./LiveProfessionalChartWorkspace.css";
import { traceChartNavigation } from "./chartNavigationTrace";

let drawingSequence = 0;
const EXTENDED_QUOTE_REFRESH_MS = 15_000;

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toggleId(ids, id) {
  return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
}

function nextDrawingId(paneId) {
  drawingSequence += 1;
  return `${paneId}-drawing-${drawingSequence}`;
}

function newYorkClock(timestamp) {
  if (!timestamp) return null;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsed);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const hour = Number(values.hour);
    const minute = Number(values.minute);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return {
      weekday: values.weekday,
      minutes: hour * 60 + minute,
    };
  } catch (error) {
    return null;
  }
}

function newYorkSession(timestamp) {
  const clock = newYorkClock(timestamp);
  if (!clock) return "unknown";

  const { weekday, minutes } = clock;

  if (weekday === "Sat") return "closed";

  if (weekday === "Sun") {
    return minutes >= 20 * 60 ? "overnight" : "closed";
  }

  if (minutes < 4 * 60) return "overnight";
  if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) return "premarket";
  if (minutes >= 9 * 60 + 30 && minutes < 16 * 60) return "regular";
  if (minutes >= 16 * 60 && minutes < 20 * 60) return "after-hours";
  if (minutes >= 20 * 60 && weekday !== "Fri") return "overnight";

  return "closed";
}

function newYorkDateKey(timestamp) {
  if (!timestamp) return "unknown";
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return "unknown";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(parsed);
  } catch (error) {
    return parsed.toISOString().slice(0, 10);
  }
}

function delayedSipCloseReady(now = new Date(Date.now())) {
  const clock = newYorkClock(now.toISOString());
  if (!clock) return false;
  if (clock.weekday === "Sat" || clock.weekday === "Sun") return true;
  return clock.minutes >= 16 * 60 + 15 || clock.minutes < 9 * 60 + 30;
}

function liveSessionNavigation(
  navigation,
  latestTimestamp,
  interval,
  currentSession,
  clearInitialExtendedFutureSpace = false,
) {
  const currentNavigation = navigation || RESET_NAVIGATION;

  if (interval === "1D") {
    return currentNavigation;
  }

  if (currentSession !== "regular") {
    if (
      clearInitialExtendedFutureSpace &&
      currentNavigation.viewportEnd === null &&
      Number(currentNavigation.futureSpaceBars) > 0 &&
      newYorkSession(latestTimestamp) === "regular"
    ) {
      return {
        ...currentNavigation,
        futureSpaceBars: 0,
      };
    }

    return currentNavigation;
  }

  // During the live regular session, Latest must remain anchored to the newest
  // authenticated candle. Future space is only created by explicit user input.
  return currentNavigation;
}

function liveChartFeed(primaryFeed, session) {
  if (session === "overnight") {
    return "boats";
  }

  if (session === "premarket" || session === "after-hours") {
    return "delayed_sip";
  }

  return primaryFeed;
}

function snapshotForChartFeed(snapshot, chartFeed) {
  if (!snapshot || !chartFeed || chartFeed === snapshot?.source?.feed) {
    return snapshot;
  }

  const quoteLabel =
    snapshot?.source?.label || "IEX — Real-Time Limited Coverage";

  if (chartFeed === "boats") {
    return {
      ...snapshot,
      source: {
        ...snapshot.source,
        feed: "boats",
        label: `Quote: ${quoteLabel} · Chart: BOATS — Overnight Limited Coverage`,
        is_delayed: false,
        limited_coverage: true,
        quality_state: "overnight_limited_coverage_chart",
        quality_level: "Overnight limited coverage chart",
        confidence_adjustment_required: true,
        nina_confidence_handling:
          "reduce_for_overnight_limited_coverage",
        nina_confidence_reasons: [
          "overnight_session",
          "limited_venue_coverage",
        ],
        nina_data_quality_notice:
          "Chart candles use BOATS overnight-session data with limited venue coverage. NINA must not treat them as full consolidated U.S. market coverage.",
      },
    };
  }

  return {
    ...snapshot,
    source: {
      ...snapshot.source,
      feed: chartFeed,
      label: `Quote: ${quoteLabel} · Chart: SIP — 15 Minute Delayed`,
      is_delayed: true,
      limited_coverage: false,
      quality_state: "delayed_consolidated_chart",
      quality_level: "Delayed consolidated chart",
      nina_confidence_handling: "reduce_for_delay",
      nina_confidence_reasons: ["delayed_market_data"],
      nina_data_quality_notice:
        "Chart candles are consolidated SIP data delayed by approximately 15 minutes; timing-sensitive conclusions must use lower confidence.",
    },
  };
}

function consolidatedCloseFromBars(payload) {
  if (payload?.source?.feed !== "delayed_sip") return null;
  const bars = Array.isArray(payload?.bars) ? payload.bars : [];
  for (let index = bars.length - 1; index >= 0; index -= 1) {
    const price = finiteNumber(bars[index]?.c);
    if (price !== null) {
      return {
        price,
        timestamp: bars[index]?.t || null,
        source: payload.source,
      };
    }
  }
  return null;
}

function chartPriceContext(trade, snapshot, consolidatedCloseState) {
  const latestTradePrice = finiteNumber(snapshot?.latest_trade?.p);
  const primaryDailyClose = finiteNumber(snapshot?.daily_bar?.c);
  const consolidatedClose =
    consolidatedCloseState?.status === "ready"
      ? finiteNumber(consolidatedCloseState.price)
      : null;
  const regularClose = consolidatedClose ?? primaryDailyClose;
  const session = newYorkSession(snapshot?.latest_trade?.t);
  const isAfterHours = session === "after-hours";

  return {
    session,
    isAfterHours,
    latestTradePrice,
    regularClose,
    regularCloseFeed:
      consolidatedClose !== null
        ? "delayed_sip"
        : snapshot?.source?.feed || "market",
    consolidatedCloseStatus: consolidatedCloseState?.status || "idle",
    chartPrice: finiteNumber(trade?.currentPrice),
  };
}

const RESET_NAVIGATION = Object.freeze({
  viewportEnd: null,
  futureSpaceBars: 0,
});

function LiveProfessionalChartWorkspace({
  trade,
  market,
  snapshot = null,
  bundledBars = null,
  dataStatus = "loading",
  isDemoActive = false,
  basicMode = false,
  allowAuthenticatedStream = true,
  publicMarketData = false,
}) {
  const selectedSymbol = String(trade?.symbol || "PLUG").toUpperCase();
  const currentSession = newYorkSession(new Date(Date.now()).toISOString());
  const extendedInitializationKey = `${selectedSymbol}:${currentSession}`;
const initializedExtendedSessionRef = useRef(null);
const shouldClearInitialExtendedFutureSpace =
  currentSession !== "regular" &&
  initializedExtendedSessionRef.current !== extendedInitializationKey;
  const defaultShowExtendedHours =
  currentSession === "overnight" ||
  currentSession === "premarket" ||
  currentSession === "after-hours" ||
  currentSession === "closed";
  const selectedSession = newYorkSession(snapshot?.latest_trade?.t);
  const selectedSessionDate = newYorkDateKey(
    snapshot?.daily_bar?.t || snapshot?.latest_trade?.t
  );
  const primaryFeed = snapshot?.source?.feed || "";
  const selectedChartFeed = isDemoActive
    ? primaryFeed
    : liveChartFeed(primaryFeed, currentSession);
  const selectedChartSnapshot = snapshotForChartFeed(snapshot, selectedChartFeed);
  const closeDelayReady = delayedSipCloseReady();
  const shouldLoadConsolidatedClose =
    !isDemoActive &&
    primaryFeed === "iex" &&
    selectedSession === "after-hours" &&
    closeDelayReady;
  const [consolidatedCloseState, setConsolidatedCloseState] = useState({
    status: "idle",
    symbol: null,
    price: null,
    timestamp: null,
    source: null,
  });
  const [delayedSipQuoteState, setDelayedSipQuoteState] = useState({
    symbol: null,
    snapshot: null,
  });
  const shouldLoadDelayedSipQuote =
    !isDemoActive &&
    (currentSession === "premarket" || currentSession === "after-hours");

  useEffect(() => {
    let active = true;
    let timerId = null;

    if (!shouldLoadDelayedSipQuote) {
      setDelayedSipQuoteState({ symbol: selectedSymbol, snapshot: null });
      return () => {
        active = false;
      };
    }

    const loadDelayedSipQuote = () => {
      getStockSnapshot(selectedSymbol, "delayed_sip", { cacheMode: "reload", publicMarketData })
        .then((nextSnapshot) => {
          if (!active) return;
          setDelayedSipQuoteState({
            symbol: selectedSymbol,
            snapshot: nextSnapshot,
          });
        })
        .catch(() => {
          if (!active) return;
          setDelayedSipQuoteState({ symbol: selectedSymbol, snapshot: null });
        });
    };

    loadDelayedSipQuote();
    timerId = window.setInterval(loadDelayedSipQuote, EXTENDED_QUOTE_REFRESH_MS);

    return () => {
      active = false;
      if (timerId) window.clearInterval(timerId);
    };
  }, [publicMarketData, selectedSymbol, shouldLoadDelayedSipQuote]);

  useEffect(() => {
    let active = true;

    if (!shouldLoadConsolidatedClose || typeof getStockBars !== "function") {
      setConsolidatedCloseState({
        status:
          primaryFeed === "iex" && selectedSession === "after-hours" && !closeDelayReady
            ? "delay-window"
            : "idle",
        symbol: selectedSymbol,
        price: null,
        timestamp: null,
        source: null,
      });
      return () => {
        active = false;
      };
    }

    setConsolidatedCloseState({
      status: "loading",
      symbol: selectedSymbol,
      price: null,
      timestamp: null,
      source: null,
    });

    getStockBars(selectedSymbol, "1D", "delayed_sip", 20, { publicMarketData })
      .then((payload) => {
        if (!active) return;
        const consolidated = consolidatedCloseFromBars(payload);
        if (!consolidated) {
          setConsolidatedCloseState({
            status: "error",
            symbol: selectedSymbol,
            price: null,
            timestamp: null,
            source: null,
          });
          return;
        }
        setConsolidatedCloseState({
          status: "ready",
          symbol: selectedSymbol,
          price: consolidated.price,
          timestamp: consolidated.timestamp,
          source: consolidated.source,
        });
      })
      .catch(() => {
        if (!active) return;
        setConsolidatedCloseState({
          status: "error",
          symbol: selectedSymbol,
          price: null,
          timestamp: null,
          source: null,
        });
      });

    return () => {
      active = false;
    };
  }, [
    closeDelayReady,
    publicMarketData,
    primaryFeed,
    selectedSession,
    selectedSessionDate,
    selectedSymbol,
    shouldLoadConsolidatedClose,
  ]);

  const selectedPriceContext = chartPriceContext(
    trade,
    snapshot,
    consolidatedCloseState.symbol === selectedSymbol
      ? consolidatedCloseState
      : { status: "idle" }
  );

  return (
    <ProfessionalChartWorkspace
      symbol={selectedSymbol}
      basicMode={basicMode}
      renderPane={(pane, controls) => {
        const paneSymbol = String(pane.symbol || "").toUpperCase();
        const usesSelectedSnapshot = paneSymbol === selectedSymbol;
        const priceContext = usesSelectedSnapshot
          ? selectedPriceContext
          : {
              session: "unknown",
              isAfterHours: false,
              latestTradePrice: null,
              regularClose: null,
              regularCloseFeed: "market",
              consolidatedCloseStatus: "idle",
              chartPrice: null,
            };
        const paneTrade = usesSelectedSnapshot
          ? { ...trade, currentPrice: priceContext.chartPrice }
          : { ...trade, symbol: paneSymbol, currentPrice: null };
        const showAfterHoursCloseNotice =
          !isDemoActive &&
          usesSelectedSnapshot &&
          priceContext.isAfterHours &&
          priceContext.regularClose !== null;
        const latestFeed = String(snapshot?.source?.feed || "market").toUpperCase();
        const closeLabel =
          priceContext.regularCloseFeed === "delayed_sip"
            ? "Consolidated regular close"
            : `${String(priceContext.regularCloseFeed).toUpperCase()} session close`;
        const closeSourceNote =
          priceContext.regularCloseFeed === "delayed_sip"
            ? "SIP — 15 minute delayed consolidated close"
            : priceContext.consolidatedCloseStatus === "loading"
            ? "Checking delayed SIP consolidated close"
            : priceContext.consolidatedCloseStatus === "error"
            ? "Consolidated close unavailable; showing limited-coverage IEX close"
            : priceContext.consolidatedCloseStatus === "delay-window"
            ? "Consolidated close is not final until the 15-minute delay window has passed"
            : "Limited-coverage session close";
        const paneNavigation =
  !isDemoActive && usesSelectedSnapshot
    ? liveSessionNavigation(
        pane.navigation,
        snapshot?.latest_trade?.t,
        pane.interval,
        currentSession,
        shouldClearInitialExtendedFutureSpace,
      )
    : pane.navigation;

        traceChartNavigation("LiveProfessionalChartWorkspace.renderPane", {
          symbol: paneSymbol,
          paneId: pane.id,
          currentSession,
          reason: "live pane render",
          dataStatus,
          hasSnapshot: Boolean(selectedChartSnapshot),
          hasBundledBars: Boolean(bundledBars),
          paneNavigation: pane.navigation,
          controlledNavigation: paneNavigation,
          latestTradeAt: selectedChartSnapshot?.latest_trade?.t || null,
        });

        return (
          <div
            className={showAfterHoursCloseNotice ? "liveProfessionalChartSessionClosed" : ""}
          >
            {showAfterHoursCloseNotice && (
              <div
                className="liveProfessionalChartSessionNotice"
                data-testid={`chart-session-price-notice-${pane.id}`}
                role="status"
              >
                <strong>
                  {closeLabel} ${priceContext.regularClose.toFixed(2)}
                </strong>
                <span>
                  {closeSourceNote}
                  {priceContext.latestTradePrice !== null
                    ? ` · Latest ${latestFeed} after-hours trade $${priceContext.latestTradePrice.toFixed(2)}`
                    : " · After-hours trade unavailable"}
                  {snapshot?.source?.limited_coverage ? " · limited coverage" : ""}
                </span>
              </div>
            )}
            <ProfessionalChart
              key={`${pane.id}-${currentSession}`}
              symbol={paneSymbol}
              currentPrice={usesSelectedSnapshot ? priceContext.chartPrice : null}
              entry={basicMode ? null : usesSelectedSnapshot ? trade?.entry ?? null : null}
              stop={basicMode ? null : usesSelectedSnapshot ? trade?.stop ?? null : null}
              target1={basicMode ? null : usesSelectedSnapshot ? trade?.targets?.target1 ?? null : null}
              target2={basicMode ? null : usesSelectedSnapshot ? trade?.targets?.target2 ?? null : null}
              market={market}
              trade={paneTrade}
              snapshot={
                !isDemoActive && usesSelectedSnapshot ? selectedChartSnapshot : null
              }
              quoteSnapshot={
                !isDemoActive && usesSelectedSnapshot ? snapshot : null
              }
              delayedSipQuoteSnapshot={
                !isDemoActive &&
                usesSelectedSnapshot &&
                delayedSipQuoteState.symbol === selectedSymbol
                  ? delayedSipQuoteState.snapshot
                  : null
              }
              bundledBars={
                !isDemoActive && usesSelectedSnapshot ? bundledBars : null
              }
              dataStatus={
                isDemoActive
                  ? "simulated"
                  : usesSelectedSnapshot
                  ? dataStatus
                  : "error"
              }
              defaultShowExtendedHours={defaultShowExtendedHours}
              fixed24HourTimelineAtLatest={false}
              allowAuthenticatedStream={allowAuthenticatedStream}
              publicMarketData={publicMarketData}
              backgroundRefreshMs={60_000}
              activeCandleRefreshMs={15_000}
              controlledTimeframe={pane.interval}
              controlledHistoryRange={pane.range}
              controlledNavigation={paneNavigation}
              activeIndicatorIds={basicMode ? [] : pane.indicators}
              drawings={basicMode ? [] : pane.drawings}
              selectedDrawingToolId={basicMode ? null : controls.selectedDrawingToolId}
              onTimeframeChange={(interval) =>
                controls.updatePane({ interval, navigation: RESET_NAVIGATION })
              }
              onHistoryRangeChange={(range) =>
                controls.updatePane({ range, navigation: RESET_NAVIGATION })
              }
              onNavigationChange={(navigation) => {
                if (currentSession !== "regular") {
  initializedExtendedSessionRef.current = extendedInitializationKey;
}
                traceChartNavigation(
                  "LiveProfessionalChartWorkspace.onNavigationChange",
                  {
                    symbol: paneSymbol,
                    paneId: pane.id,
                    currentSession,
                    reason: "ProfessionalChart navigation callback",
                    previousNavigation: pane.navigation,
                    nextNavigation: navigation,
                  },
                  { stack: true }
                );
                controls.updatePane({ navigation });
              }}
              onToggleIndicator={(indicatorId) =>
                controls.updatePane({
                  indicators: toggleId(pane.indicators, indicatorId),
                })
              }
              onCreateDrawing={({ toolId, points }) =>
                controls.updatePane({
                  drawings: drawingReducer(pane.drawings, {
                    type: "ADD_DRAWING",
                    drawing: {
                      id: nextDrawingId(pane.id),
                      paneId: pane.id,
                      toolId,
                      points,
                    },
                  }),
                })
              }
              onSelectDrawing={(drawingId) =>
                controls.updatePane({
                  drawings: drawingReducer(pane.drawings, {
                    type: "SELECT_DRAWING",
                    drawingId,
                  }),
                })
              }
            />
          </div>
        );
      }}
    />
  );
}

export default LiveProfessionalChartWorkspace;
